import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { User, Shield, Trash2, Settings as SettingsIcon, Mail, MessageCircle, LogOut, Camera, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useNavigate } from 'react-router-dom'
import { Header } from '@/components/Header'

const Settings = () => {
  const [user, setUser] = useState<any>(null)
  const [profile, setProfile] = useState<any>(null)
  const [displayName, setDisplayName] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
      filter: 'blur(6px)'
    },
    visible: { 
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        type: 'spring' as const,
        bounce: 0.3,
        duration: 0.8,
      },
    },
  }

  useEffect(() => {
    loadUserData()
  }, [])

  const loadUserData = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      if (error) throw error
      
      setUser(user)

      // Load profile data
      if (user) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single()
        
        if (profileData) {
          setProfile(profileData)
          setDisplayName(profileData.display_name || '')
          // If avatar_url exists, get the public URL from Supabase Storage
          if (profileData.avatar_url) {
            if (profileData.avatar_url.startsWith('http')) {
              // External URL
              setAvatarUrl(profileData.avatar_url)
            } else {
              // Supabase Storage path
              const { data } = supabase.storage.from('avatars').getPublicUrl(profileData.avatar_url)
              setAvatarUrl(data.publicUrl)
            }
          } else {
            setAvatarUrl('')
          }
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error)
      toast({
        title: "Error",
        description: "Failed to load user data",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return
    }

    setUpdating(true)
    try {
      // Note: Account deletion would typically require backend logic
      toast({
        title: "Account Deletion",
        description: "Please contact support to delete your account",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete account",
        variant: "destructive",
      })
    } finally {
      setUpdating(false)
    }
  }

  const uploadAvatar = async (file: File) => {
    if (!user) return null

    setUploading(true)
    try {
      // Validate file type and size
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Please upload a valid image file (JPEG, PNG, WebP, or GIF)')
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        throw new Error('File size must be less than 5MB')
      }

      // Create file path: user_id/filename
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/avatar.${fileExt}`

      // Delete existing avatar if it exists
      if (profile?.avatar_url && !profile.avatar_url.startsWith('http')) {
        await supabase.storage.from('avatars').remove([profile.avatar_url])
      }

      // Upload new avatar
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { 
          cacheControl: '3600',
          upsert: true 
        })

      if (error) throw error

      return data.path
    } catch (error: any) {
      toast({
        title: "Upload Error",
        description: error.message,
        variant: "destructive",
      })
      return null
    } finally {
      setUploading(false)
    }
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const avatarPath = await uploadAvatar(file)
    if (avatarPath) {
      // Update profile with new avatar path
      await handleUpdateProfile(avatarPath)
      // Update local avatar URL for display
      const { data } = supabase.storage.from('avatars').getPublicUrl(avatarPath)
      setAvatarUrl(data.publicUrl)
    }
  }

  const handleUpdateProfile = async (newAvatarPath?: string) => {
    if (!user) return
    
    setUpdating(true)
    try {
      const updateData: any = { 
        display_name: displayName.trim() || null
      }

      // Only update avatar_url if a new path is provided
      if (newAvatarPath !== undefined) {
        updateData.avatar_url = newAvatarPath
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('user_id', user.id)
      
      if (error) throw error
      
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated",
      })
      
      // Reload profile data
      loadUserData()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      })
    } finally {
      setUpdating(false)
    }
  }

  const handleLogout = async () => {
    setUpdating(true)
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      
      toast({
        title: "Logged out",
        description: "You have been successfully logged out",
      })
      
      // Redirect to landing page after logout
      navigate('/')
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log out",
        variant: "destructive",
      })
    } finally {
      setUpdating(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <SettingsIcon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold font-poppins">Settings</h1>
                <p className="text-muted-foreground font-inter">Manage your account and preferences</p>
              </div>
            </div>
          </motion.div>

          {/* Profile */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  <CardTitle className="font-poppins">Profile</CardTitle>
                </div>
                <CardDescription className="font-inter">
                  Customize how you appear to others
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Picture */}
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <Avatar className="h-20 w-20">
                      <AvatarImage src={avatarUrl} />
                      <AvatarFallback className="text-lg">
                        {displayName ? displayName.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <Button
                      size="sm"
                      variant="outline"
                      className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full p-0"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      ) : (
                        <Camera className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-inter">Profile Picture</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="font-inter"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload Image'}
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground font-inter">
                      Upload a JPEG, PNG, WebP or GIF file (max 5MB)
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Display Name */}
                <div className="space-y-2">
                  <Label htmlFor="display-name" className="font-inter">Display Name</Label>
                  <Input
                    id="display-name"
                    type="text"
                    placeholder="Your display name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="font-inter"
                    maxLength={50}
                  />
                  <p className="text-sm text-muted-foreground font-inter">
                    This name will be shown in your comments and interactions
                  </p>
                </div>

                {/* Save Button */}
                <Button 
                  onClick={() => handleUpdateProfile()}
                  disabled={updating}
                  className="font-inter"
                >
                  {updating ? 'Saving...' : 'Save Changes'}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Account Information */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <CardTitle className="font-poppins">Account Information</CardTitle>
                </div>
                <CardDescription className="font-inter">
                  Your basic account details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-inter">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="font-inter"
                    />
                    <p className="text-sm text-muted-foreground font-inter">
                      Email cannot be changed. Contact support if needed.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="created-at" className="font-inter">Account Created</Label>
                    <Input
                      id="created-at"
                      value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      disabled
                      className="font-inter"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact & Support */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-primary" />
                  <CardTitle className="font-poppins">Contact & Support</CardTitle>
                </div>
                <CardDescription className="font-inter">
                  Get help, share feedback, or report issues
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center p-6 rounded-lg border bg-muted/50">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Mail className="h-5 w-5 text-primary" />
                    <h4 className="text-lg font-semibold font-inter">Email Support</h4>
                  </div>
                  <p className="text-muted-foreground font-inter mb-4">
                    Questions, feedback, or need help? We're here for you.
                  </p>
                  <p className="text-lg">
                    📧 <a 
                      href="mailto:paulofhreire@gmail.com" 
                      className="text-primary hover:underline font-medium"
                    >
                      paulofhreire@gmail.com
                    </a>
                  </p>
                  <p className="text-sm text-muted-foreground font-inter mt-2">
                    We typically respond within 24 hours
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Security */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <CardTitle className="font-poppins">Security</CardTitle>
                </div>
                <CardDescription className="font-inter">
                  Account security and authentication
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="space-y-1">
                    <h4 className="font-medium font-inter">Password</h4>
                    <p className="text-sm text-muted-foreground font-inter">
                      Last updated: {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="font-inter">
                    Reset Password
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <LogOut className="h-4 w-4 text-muted-foreground" />
                      <h4 className="font-medium font-inter">Sign Out</h4>
                    </div>
                    <p className="text-sm text-muted-foreground font-inter">
                      Sign out of your account on this device
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleLogout}
                    disabled={updating}
                    className="font-inter"
                  >
                    {updating ? 'Signing out...' : 'Sign Out'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Danger Zone */}
          <motion.div variants={itemVariants}>
            <Card className="border-destructive/20">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-destructive" />
                  <CardTitle className="text-destructive font-poppins">Danger Zone</CardTitle>
                </div>
                <CardDescription className="font-inter">
                  Irreversible account actions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 rounded-lg border border-destructive/20 bg-destructive/5">
                  <div className="space-y-1">
                    <h4 className="font-medium text-destructive font-inter">Delete Account</h4>
                    <p className="text-sm text-muted-foreground font-inter">
                      Permanently delete your account and all associated data
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteAccount}
                    disabled={updating}
                    className="font-inter"
                  >
                    {updating ? 'Processing...' : 'Delete Account'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </main>
    </div>
  )
}

export default Settings